/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';
import {
  defaultClearEmailBody,
  defaultClearEmailSubject,
  defaultClearTextMessage,
  defaultTriggerEmailBody,
  defaultTriggerEmailSubject,
  defaultTriggerTextMessage,
} from '../../../utility/AlertNotificationTemplateUtils';

const notificationTemplatesApiUrl = '/api/v1/notification-templates/';

// eslint-disable-next-line max-len
const selectNotificationTemplatesCrudOperation = (state) => state.notificationTemplates.crudOperation;
const selectNotificationTemplatesCrudStatus = (state) => state.notificationTemplates.crudStatus;
const selectNotificationTemplatesInitialLoad = (state) => state.notificationTemplates.initialLoad;
const selectNotificationTemplatesError = (state) => state.notificationTemplates.error;
// eslint-disable-next-line max-len
const selectActiveNotificationTemplate = (state) => state.notificationTemplates.activeNotificationTemplate;
const selectNotificationTemplatesErrors = (state) => state.notificationTemplates.errors;

const selectNotificationTemplates = createSelector(
  (state) => state.notificationTemplates, (slice) => slice.notificationTemplateList,
);
const selectNotificationTemplateById = createSelector(
  [selectNotificationTemplates,
    (templateList, id) => id],
  (templateList, templateId) => templateList.find((template) => template.id === templateId),
);

const fetchNotificationTemplate = createAsyncThunk(
  'securedApi/notificationTemplates/fetchSaved',
  async (id, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${envConfig.apiUrl}${notificationTemplatesApiUrl}${id}`);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const fetchNotificationTemplates = createAsyncThunk(
  'securedApi/notificationTemplates/fetchNotificationTemplates',
  async (searchType, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${envConfig.apiUrl}${notificationTemplatesApiUrl}`);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const editNotificationTemplate = createAsyncThunk(
  'securedApi/notificationTemplates/editNotificationTemplate',
  async (editData, thunkAPI) => {
    const { notificationTemplate, patchFields } = editData;
    try {
      await RaaAuthenticatedRemoteService.globalInstance()
        .patch(`${envConfig.apiUrl}${notificationTemplatesApiUrl}${notificationTemplate.id}`,
          { data: { ...notificationTemplate, patchFields: [...patchFields] } });
      return thunkAPI.fulfillWithValue({ ...notificationTemplate });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const createNotificationTemplate = createAsyncThunk(
  'securedApi/notificationTemplates/createNotificationTemplate',
  async (data, thunkAPI) => {
    const { notificationTemplate } = data;
    try {
      const response = await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.apiUrl}/api/v1/notification-templates/`,
          { data: { ...notificationTemplate } });
      return thunkAPI.fulfillWithValue({ ...response.notificationTemplate });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.data);
    }
  },
);

const deleteNotificationTemplate = createAsyncThunk(
  'securedApi/notificationTemplates/deleteNotificationTemplate',
  async (id, thunkAPI) => {
    try {
      await RaaAuthenticatedRemoteService.globalInstance()
        .delete(`${envConfig.apiUrl}/api/v1/notification-templates/${id}`);
      return thunkAPI.fulfillWithValue(id);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const initialState = {
  notificationTemplateList: [
    {
      id: 1,
      name: 'my template',
      uniqueAlertCount: 2,
      useDefaultEmailTemplate: true,
      useDefaultSmsTemplate: true,
      triggeredEmailSubject: defaultTriggerEmailSubject,
      triggeredEmailBody: defaultTriggerEmailBody,
      triggeredSmsBody: defaultTriggerTextMessage,
      clearedEmailSubject: defaultClearEmailSubject,
      clearedEmailBody: defaultClearEmailBody,
      clearedSmsBody: defaultClearTextMessage,
    },
  ],
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  error: false,
  loading: true,
  initialLoad: true,
  activeNotificationTemplate: null,
  errors: [],
};

export const notificationTemplatesSlice = createSlice({
  name: 'notificationTemplates',
  initialState,
  reducers: {
    clearNotificationTemplatesSlice: () => initialState,
    setActiveNotificationTemplate: (state, action) => {
      state.activeNotificationTemplate = action.payload;
    },
    removeNotificationTemplate: (state, action) => {
      state.notificationTemplateList = [...state.notificationTemplateList]
        .filter((search) => search.id !== action.payload);
    },
    setNotificationTemplateCrudType: (state, action) => {
      state.crudOperation = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationTemplate.pending, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(fetchNotificationTemplate.fulfilled, (state, action) => {
        const { notificationTemplate } = action.payload;
        const foundIndex = state.notificationTemplateList
          .findIndex((range) => range.id === notificationTemplate.id);
        if (foundIndex !== -1) {
          state.notificationTemplateList.splice(foundIndex, 1, notificationTemplate);
        } else {
          state.notificationTemplateList.push(notificationTemplate);
        }
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(fetchNotificationTemplate.rejected, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(fetchNotificationTemplates.pending, (state) => {
        state.loading = true;
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(fetchNotificationTemplates.fulfilled, (state, action) => {
        const { notificationTemplateList } = action.payload || [];
        state.notificationTemplateList = notificationTemplateList;
        state.crudStatus = CrudStatus.SUCCESS;
        state.loading = false;
        state.error = false;
        state.initialLoad = false;
      })
      .addCase(fetchNotificationTemplates.rejected, (state) => {
        state.error = true;
        state.loading = false;
        state.crudStatus = CrudStatus.ERROR;
      })
      .addCase(editNotificationTemplate.pending, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(editNotificationTemplate.fulfilled, (state, action) => {
        const newNotificationTemplate = action.payload;
        const foundIndex = state.notificationTemplateList
          .findIndex((range) => range.id === newNotificationTemplate.id);
        if (foundIndex !== -1) {
          state.notificationTemplateList.splice(foundIndex, 1, newNotificationTemplate);
        } else {
          state.notificationTemplateList.push(newNotificationTemplate);
        }
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(editNotificationTemplate.rejected, (state, action) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
        state.errors = [action.payload];
      })
      .addCase(createNotificationTemplate.pending, (state) => {
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(createNotificationTemplate.fulfilled, (state, action) => {
        const newNotificationTemplate = action.payload;
        const foundIndex = state.notificationTemplateList
          .findIndex((range) => range.id === newNotificationTemplate.id);
        if (foundIndex !== -1) {
          state.notificationTemplateList.splice(foundIndex, 1, newNotificationTemplate);
        } else {
          state.notificationTemplateList.push(newNotificationTemplate);
        }
        state.activeNotificationTemplate = newNotificationTemplate;
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(createNotificationTemplate.rejected, (state, action) => {
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
        state.errors = action.payload;
      })
      .addCase(deleteNotificationTemplate.pending, (state) => {
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(deleteNotificationTemplate.fulfilled, (state, action) => {
        state.notificationTemplateList = [...state.notificationTemplateList]
          .filter((search) => search.id !== action.payload);
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(deleteNotificationTemplate.rejected, (state) => {
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      });
  },
});

const { actions, reducer } = notificationTemplatesSlice;
export const {
  clearNotificationTemplatesSlice, removeNotificationTemplate,
  setActiveNotificationTemplate, setNotificationTemplateCrudType,
} = actions;
export {
  selectNotificationTemplates,
  fetchNotificationTemplates,
  selectNotificationTemplateById,
  editNotificationTemplate,
  createNotificationTemplate,
  deleteNotificationTemplate,
  selectNotificationTemplatesCrudOperation,
  selectNotificationTemplatesInitialLoad,
  selectNotificationTemplatesError,
  selectNotificationTemplatesCrudStatus,
  selectActiveNotificationTemplate,
  selectNotificationTemplatesErrors,
  fetchNotificationTemplate,
};

export default reducer;
